import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';

const GlobalRewardsContext = createContext();

const rewardsReducer = (state, action) => {
  switch (action.type) {
    case 'SET_COINS':
      return { ...state, coins: action.payload };
    case 'SET_COUPONS':
      return { ...state, coupons: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'UPDATE_COINS':
      return { ...state, coins: state.coins + action.payload };
    case 'SPEND_COINS':
      return { ...state, coins: Math.max(0, state.coins - action.payload) };
    default:
      return state;
  }
};

export const GlobalRewardsProvider = ({ children }) => {
  const [user] = useAuthState(auth);
  const [state, dispatch] = useReducer(rewardsReducer, {
    coins: 0,
    coupons: [],
    loading: false
  });

  // Listen to user's rewards data in real-time
  useEffect(() => {
    if (!user) {
      dispatch({ type: 'SET_COINS', payload: 0 });
      dispatch({ type: 'SET_COUPONS', payload: [] });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const userData = docSnap.data();
        dispatch({ type: 'SET_COINS', payload: userData.coins || 0 });
        dispatch({ type: 'SET_COUPONS', payload: userData.coupons || [] });
      } else {
        dispatch({ type: 'SET_COINS', payload: 0 });
        dispatch({ type: 'SET_COUPONS', payload: [] });
      }
      dispatch({ type: 'SET_LOADING', payload: false });
    });

    return () => unsubscribe();
  }, [user]);

  const value = {
    ...state,
    updateCoins: (amount) => dispatch({ type: 'UPDATE_COINS', payload: amount }),
    spendCoins: (amount) => dispatch({ type: 'SPEND_COINS', payload: amount }),
    user
  };

  return (
    <GlobalRewardsContext.Provider value={value}>
      {children}
    </GlobalRewardsContext.Provider>
  );
};

export const useGlobalRewards = () => {
  const context = useContext(GlobalRewardsContext);
  if (!context) {
    throw new Error('useGlobalRewards must be used within a GlobalRewardsProvider');
  }
  return context;
};